<template>
  <section class="ma-4 ma-md-8">
    <t-page-title
      title="Promoters | Gerenciar"
      :on-add="() => $router.push({ name: 'create-affiliates' })"
    />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
        >
          <template #[`item.dates`]="{ item }">
            <span> {{ item.dates.open }}</span>
          </template>
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split("-")[0] }}</span>
          </template>
          <template #[`item.email`]="{ item }">
            <v-card readonly elevation="0" @click="copy(`${item.email}`)">
              {{ `${item.email}` }}
            </v-card>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>
import { ref, type Ref } from 'vue'
import type { AxiosResponse } from 'axios'
import type {
  DataTableHeader,
  DataTableFilter,
  DefaultDataTableActions,
} from '@/types/DataTable'
import type PaginatedResponse from '@/types/PaginatedResponse'
import DataTable from '@/components/datatable/DataTable.vue'
import TPageTitle from '@/components/TPageTitle.vue'
import AffiliateService from '@/services/AffiliateService'
import type Affiliate from '@/types/Affiliate'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSnackBar } from '@/stores/snackbar'
import { useLoader } from '@/stores/loader'
import { useDialog } from '@/stores/dialog'
import { useAbility } from '@casl/vue'
import { useRouter } from 'vue-router'

// AffiliateService.index() actually responds with a `dates` field (used by
// the template below), but the shared Affiliate type doesn't declare it;
// TServersideDatatable never caught this because it typed items as
// `unknown[]`. Narrowly extended here rather than widening the shared type.
interface AffiliateListItem extends Affiliate {
  dates: { open: string }
}

const { can } = useAbility()
const router = useRouter()
const snackbar = useSnackBar()
const loader = useLoader()
const dialog = useDialog()

const resource = 'affiliates'

const headers: DataTableHeader[] = [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  { title: 'Nome', value: 'name', key: 'name', sortable: false },
  { title: 'E-mail', value: 'email', key: 'email', sortable: false },
  { title: 'Tag', value: 'tag', key: 'tag', sortable: false },
  {
    title: 'Ações',
    value: 'actions',
    key: 'actions',
    sortable: false,
    align: 'center',
  },
  {
    title: 'Ingressos Vendidos',
    value: 'sales_quantity',
    key: 'sales_quantity',
    sortable: false,
  },
]

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({
    label: 'Nome',
    field: 'name',
    icon: 'fa-regular fa-signature',
    required: false,
  }),
])

const actions: DefaultDataTableActions<Affiliate> = {
  read: {
    callback: async item => router.push({
      name: 'read-affiliate',
      params: { id: item.id },
    }),
    condition: () => can('read', 'affiliates'),
    icon: 'fa-solid fa-magnifying-glass',
    color: 'primary',
  },
  update: {
    callback: item => router.push({
      name: 'edit-affiliates',
      params: { id: item.id },
    }),
    condition: () => can('update', 'affiliates'),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: async item => destroy(item),
    condition: () => can('delete', 'affiliates'),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
}

async function fetch(page: number, filtersString: string, per_page?: number): Promise<AxiosResponse<PaginatedResponse<AffiliateListItem>>> {
  try {
    return await new AffiliateService().index(page, filtersString, per_page) as unknown as AxiosResponse<PaginatedResponse<AffiliateListItem>>
  } catch (error) {
    snackbar.show('Não foi possível buscar os promoters.', 'error')
    throw error
  }
}

async function destroy(model: Affiliate) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir este promoter? Essa ação é irreversível',
  })

  if (!confirm) return

  try {
    loader.increment()

    await new AffiliateService().destroy(model)

    router.go(0)
  } catch (error) {
    snackbar.show('Não foi possível remover o promoter.', 'error')
    throw error
  } finally {
    loader.decrement()
  }
}

function copy(text: string) {
  navigator.clipboard.writeText(text)
  snackbar.show('Conteúdo copiado', 'success')
}
</script>
